package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceReasonTooLongException
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.util.AppClock.now
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.RequestSettings
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.requests.RequestCounters

/**
 * An employee reports an absence; the workplace's managers are notified. Health minimisation
 * (SH-50, art. 22¹ KP): the notification never names the absence type (sick leave is health data,
 * and the push text shows on lock screens); managers see the details in the app only. The note is
 * optional, short ([REASON_MAX_LENGTH]) and the form asks for no medical details.
 */
class ReportAbsenceUseCase(
    private val absenceRepository: AbsenceRepository,
    private val userRepository: UserRepository,
    /** SH-223: the workplace's request settings and market, for the yearly limits; without it the defaults. */
    private val workplaceRepository: WorkplaceRepository? = null,
    /** SH-223: the person's approved overtime, for time off in lieu; without it no balance (refused). */
    private val workRequestRepository: WorkRequestRepository? = null,
    /** SH-223: frees the shifts of a request a manager approves at once; without it they stay. */
    private val resolveConflicts: ResolveAbsenceConflictsUseCase? = null
) {
    suspend operator fun invoke(
        userId: String,
        workplaceId: String?, // Null = dotyczy wszystkich miejsc pracy
        type: AbsenceType,
        startDate: String,
        endDate: String,
        reason: String?,
        /** SH-57: sick leave paid at 100% (ignored for other types). */
        sickPayFull: Boolean = false
    ): Result<String> = submit(
        Absence(
            id = "", userId = userId, workplaceId = workplaceId, type = type, status = AbsenceStatus.PENDING,
            startDate = startDate, endDate = endDate, reason = reason, attachmentUrl = null, processedBy = null,
            processedAt = null, sickPayFull = sickPayFull, metadata = AuditMetadata(createdAt = "", createdBy = userId)
        )
    )

    /**
     * SH-223: sends [draft] (any type, its details filled in) as a PENDING request, after the
     * type's limits (RequestCounters.check, against every request not rejected): over a limit it is
     * refused with [RequestLimitException]. The unit of childcare and force majeure is the year's
     * (set by its first request), time off in lieu stores the overtime hours it gives back.
     */
    suspend fun submit(
        draft: Absence, actingUserId: String = draft.userId, approveNow: Boolean = false,
        /** Time off in lieu the employer grants unasked (1.5 hours off per overtime hour); a manager's entry is usually the person's own ask. */
        employerGranted: Boolean = false
    ): Result<String> {
        val note = draft.reason?.trim()?.takeIf { it.isNotEmpty() }
        if (note != null && note.length > REASON_MAX_LENGTH) {
            return Result.failure(AbsenceReasonTooLongException(REASON_MAX_LENGTH))
        }
        if (draft.endDate < draft.startDate || (draft.isHourly && draft.startDate != draft.endDate)) {
            return Result.failure(IllegalArgumentException("A request in hours covers one day"))
        }
        return try {
            val nowStr = now().toString()
            var absence = draft.copy(
                id = "",
                status = AbsenceStatus.PENDING, // Domyślnie oczekuje na zatwierdzenie
                reason = note,
                processedBy = null,
                processedAt = null,
                rejectionReason = null,
                overrideNote = null,
                sickPayFull = draft.sickPayFull && (draft.type == AbsenceType.SICK_LEAVE || draft.type == AbsenceType.QUARANTINE),
                startTime = draft.startTime?.takeIf { draft.isHourly },
                endTime = draft.endTime?.takeIf { draft.isHourly },
                occasion = draft.occasion?.takeIf { draft.type == AbsenceType.OCCASIONAL_LEAVE },
                careFor = draft.careFor?.takeIf { draft.type == AbsenceType.CARE_ALLOWANCE },
                children = draft.children?.takeIf { draft.type == AbsenceType.MATERNITY || draft.type == AbsenceType.PARENTAL }?.coerceIn(1, 10),
                metadata = AuditMetadata(createdAt = nowStr, createdBy = actingUserId, updatedAt = null, updatedBy = null)
            )
            val workplace = draft.workplaceId?.let { workplaceRepository?.getWorkplace(it)?.getOrNull() }
            // SH-223: the workplace's rule of the type and the person's contract that day.
            if (workplace != null) {
                val financial = userRepository.getSensitiveUserData(draft.userId).getOrNull()?.financialEngagements?.get(workplace.id)
                if (!com.evoapps.evoshift.domain.requests.RequestEligibility.allows(
                        workplace.settings.requests, com.evoapps.evoshift.domain.requests.RequestType.Leave(absence.type), financial, absence.startDate)
                ) return Result.failure(com.evoapps.evoshift.domain.requests.RequestNotAllowedException(absence.type.name))
            }
            if (absence.type in LIMITED_TYPES) {
                val settings = workplace?.settings?.requests ?: RequestSettings()
                val rules = PayrollRules.of(workplace?.settings?.market ?: Market.POLAND)
                val existing = absenceRepository.getAbsencesForUser(draft.userId).getOrDefault(emptyList())
                val year = absence.startDate.take(4).toIntOrNull() ?: 0
                if (absence.type == AbsenceType.CHILDCARE || absence.type == AbsenceType.FORCE_MAJEURE) {
                    absence = absence.copy(
                        limitUnit = RequestCounters.yearUnit(absence.type, absence.userId, absence.workplaceId, year, existing)
                            ?: RequestCounters.ownUnit(absence.copy(limitUnit = null))
                    )
                }
                val overtime = if (absence.type == AbsenceType.TIME_OFF_IN_LIEU) {
                    absence = absence.copy(
                        compensatedOvertimeHours = RequestCounters.compensatedHours(
                            absence.copy(compensatedOvertimeHours = null), rules, grantedByEmployer = employerGranted
                        )
                    )
                    workRequestRepository?.forUser(draft.userId)?.getOrNull().orEmpty()
                } else emptyList()
                val check = RequestCounters.check(absence, existing, settings, rules, overtime)
                if (check != LimitCheck.Ok) return Result.failure(RequestLimitException(check))
            }

            // SH-223: a manager's entry for someone else may be approved at once (the shifts it overlaps freed first).
            val onBehalf = actingUserId != draft.userId
            if (approveNow && onBehalf) {
                absence.workplaceId?.let { wp -> resolveConflicts?.invoke(wp, absence)?.getOrThrow() }
                absence = absence.copy(status = AbsenceStatus.APPROVED, processedBy = actingUserId, processedAt = nowStr)
            }
            val id = absenceRepository.createAbsence(absence).getOrThrow()
            if (onBehalf) {
                notifyOnBehalf(userRepository, absence.workplaceId, absence.userId, actingUserId, absence.startDate, absence.endDate,
                    id, NotificationData.ABSENCE_ID, approved = absence.status == AbsenceStatus.APPROVED)
            } else {
                notifyManagers(absence, id, NotificationData.ABSENCE_ID)
            }
            Result.success(id)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Notifies the workplace's managers of a new request ([idKey]: the request's id's key). The
     * text never names the type (health data, lock screens).
     */
    internal suspend fun notifyManagers(absence: Absence, id: String, idKey: String) =
        notifyManagers(userRepository, absence.workplaceId, absence.userId, absence.startDate, absence.endDate, id, idKey)

    companion object {
        const val REASON_MAX_LENGTH = 200

        /** The types whose request is checked against a limit before it is sent. */
        val LIMITED_TYPES = setOf(
            AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE, AbsenceType.CARER_LEAVE, AbsenceType.TIME_OFF_IN_LIEU,
            AbsenceType.BLOOD_DONATION, AbsenceType.OCCASIONAL_LEAVE
        )

        /**
         * SH-223: a request a manager ([actingUserId]) filed for [userId]: the person is told, and
         * the other approvers when it still waits for a decision; never the manager who filed it.
         */
        suspend fun notifyOnBehalf(
            userRepository: UserRepository, workplaceId: String?, userId: String, actingUserId: String, startDate: String, endDate: String,
            id: String, idKey: String, approved: Boolean
        ) {
            val manager = userRepository.getUserProfile(actingUserId).getOrNull()
            val nowStr = now().toString()
            userRepository.sendNotification(
                Notification(
                    userId = userId,
                    title = "A request was filed for you",
                    message = "${manager?.displayName ?: "A manager"} filed a request on your behalf for $startDate - $endDate" +
                        if (approved) " and approved it." else ".",
                    type = NotificationType.GENERAL,
                    createdAt = nowStr,
                    data = buildMap {
                        put(idKey, id)
                        workplaceId?.let { put(NotificationData.WORKPLACE_ID, it) }
                        put(NotificationData.KIND, NotificationData.Kind.REQUEST_ON_BEHALF)
                        put(NotificationData.NAME, manager?.displayName ?: "")
                        put(NotificationData.DATE, startDate)
                        put(NotificationData.END, endDate)
                    }
                )
            )
            if (!approved) notifyManagers(userRepository, workplaceId, userId, startDate, endDate, id, idKey, except = actingUserId)
        }

        /** SH-223: shared with work requests: one notification per manager, the type unnamed. */
        suspend fun notifyManagers(
            userRepository: UserRepository, workplaceId: String?, userId: String, startDate: String, endDate: String, id: String, idKey: String,
            /** SH-223: the manager who filed it, not told of their own entry. */
            except: String? = null
        ) {
            if (workplaceId == null) return
            val employees = userRepository.getAssignableEmployees(workplaceId).getOrNull() ?: emptyList()
            val user = userRepository.getUserProfile(userId).getOrNull()
            // Whoever decides absence requests (the Requests screen), financial managers too.
            val managers = employees.filter { it.engagements[workplaceId]?.permission?.canEditRoster() == true && it.uid != except }
            val nowStr = now().toString()
            managers.forEach { manager ->
                userRepository.sendNotification(
                    Notification(
                        userId = manager.uid,
                        title = "New absence request",
                        message = "${user?.displayName ?: "An employee"} sent an absence request for $startDate - $endDate.",
                        type = NotificationType.GENERAL,
                        createdAt = nowStr,
                        data = mapOf(
                            idKey to id,
                            NotificationData.WORKPLACE_ID to workplaceId,
                            NotificationData.KIND to NotificationData.Kind.ABSENCE_REQUEST,
                            NotificationData.NAME to (user?.displayName ?: ""),
                            NotificationData.SUBJECT_USER_ID to userId,
                            NotificationData.DATE to startDate,
                            NotificationData.END to endDate
                        )
                    )
                )
            }
        }
    }
}

/** SH-223: a request over its type's limit, or not fitting its rules ([check] says which). */
class RequestLimitException(val check: LimitCheck) : Exception("The request does not fit its limit: $check")
